import styled from '@mui/material/styles/styled';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { findLast, first, last } from 'lodash-es';
import { alpha, Tooltip, Typography } from '@mui/material';
import {
  abbreviateNumber,
  formatNumberForLocale,
} from '../../utils/stringHelpers';
import {
  useGetCurrentUserLevel,
  useGetNextUserLevel,
} from '../../stores/userActions';

const StyledProgressBar = styled('div')(({ theme }) => ({
  position: 'absolute',
  backgroundColor: theme.palette.primary.main,
  height: 6,
  bottom: 0,
  borderRadius: 8,
  transition: 'width 1s linear',
}));

const StyledTimelineDot = styled('div', {
  shouldForwardProp: (prop) => !['customStyles'].includes(prop),
})(({ customStyles, theme }) => ({
  position: 'absolute',
  top: 0,
  height: '100%',

  '& .dot': {
    position: 'absolute',
    '&:after': {
      content: "''",
      borderRadius: '50%',
      borderStyle: 'solid',
      borderWidth: 2,
      borderColor: 'var(--progressbar)',
      position: 'absolute',
      bottom: customStyles.bottom || -5,
      height: customStyles.height || 16,
      width: customStyles.width || 16,
      backgroundColor: 'var(--Bodybackground)',
      transform: 'translateX(-50%)',
      transition: 'background-color .3s,border-color .3s',
    },
    '&:hover': {
      '&:after': {
        backgroundColor: 'var(--progressbar)',
        cursor: 'pointer',
      },
    },
    '&.current': {
      '&:after': {
        boxShadow: `0 0 0 0 ${theme.palette.secondary.main}`,
        transform: 'scale(1)',
        animation: 'pulse 2s infinite',
      },
    },
  },
  '& .dot-text': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 8,
    transform: 'translateX(-50%)',

    '& .MuiTypography-root': {
      fontSize: customStyles.fontSize,
      fontWeight: customStyles.fontWeight,
    },
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: `0 0 0 0 ${alpha(theme.palette.secondary.main, 0.7)}`,
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: `0 0 0 10px ${alpha(theme.palette.secondary.main, 0)}`,
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: `0 0 0 0 ${alpha(theme.palette.secondary.main, 0)}`,
    },
  },
}));

function TimelineBar(props) {
  const { items, prevValue = -1, value, customStyles = {} } = props;
  const currentLevel = useGetCurrentUserLevel();
  const nextLevel = useGetNextUserLevel();
  const pointsOffsetRatio = currentLevel === nextLevel ? 1 : 0.9;
  const cappedValue = Math.min(value, nextLevel.points * pointsOffsetRatio);
  const cappedPrevValue = Math.min(
    prevValue,
    nextLevel.points * pointsOffsetRatio
  );

  const getPercentProgress = (val) => {
    const lastItem = last(items);

    const stepValue = 100 / (items.length - 1);
    const offset = stepValue * currentLevel.index;

    if (currentLevel === lastItem) {
      return 100;
    }

    return (
      offset +
      ((val - currentLevel.points) / (nextLevel.points - currentLevel.points)) *
        stepValue
    );
  };

  const [percentProgress, setPercentProgress] = useState(
    getPercentProgress(cappedPrevValue >= 0 ? cappedPrevValue : cappedValue)
  );

  const step = 100 / (items.length - 1);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPercentProgress(getPercentProgress(cappedValue));
    }, 1000);
    return () => clearTimeout(timeout);
  });

  return (
    <Box height={32} width="90%" margin="0 auto">
      <Box width="100%" height={6} sx={{ backgroundColor: 'lightgray' }} />
      <Box position="relative">
        <StyledProgressBar style={{ width: `${percentProgress}%` }} />
        {items.map((item) => {
          const { index, name } = item;
          const pos = `${step * index}%`;
          return (
            <Tooltip
              key={index}
              title={`${formatNumberForLocale(
                item.points
              )} points and ${formatNumberForLocale(item.badges)} badges`}>
              <StyledTimelineDot
                key={index}
                style={{
                  left: pos,
                }}
                customStyles={customStyles}>
                <div
                  className={`dot ${
                    index === currentLevel.index ? 'current' : ''
                  }`}
                />
                <div className="dot-text">
                  <Typography variant="body2" fontWeight={600}>
                    {name}
                  </Typography>
                </div>
              </StyledTimelineDot>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

TimelineBar.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      points: PropTypes.number.isRequired,
    })
  ).isRequired,
  prevValue: PropTypes.number,
  value: PropTypes.number.isRequired,
  customStyles: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    bottom: PropTypes.number,
    fontWeight: PropTypes.number,
    fontSize: PropTypes.string,
  }),
};

export default TimelineBar;
